import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * Campo de dato bancario con botón de copiar al portapapeles.
 *
 * Props:
 *  - label: string (ej "Número de Cuenta")
 *  - value: string | number
 *  - highlight: bool (estilo destacado azul/verde para campos clave)
 *  - testId: string (opcional)
 *  - copyValue: string (opcional, valor a copiar si difiere del display)
 */
export default function CopyableField({ label, value, highlight = false, testId, copyValue }) {
  const [copied, setCopied] = useState(false);
  const displayValue = value ?? '-';
  const valueToCopy = (copyValue ?? value ?? '').toString();

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!valueToCopy) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(valueToCopy);
      } else {
        // Fallback para navegadores antiguos / contexto no seguro
        const ta = document.createElement('textarea');
        ta.value = valueToCopy;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  if (highlight) {
    return (
      <div
        className="flex justify-between items-center py-3 bg-blue-50 rounded-lg px-3 -mx-3"
        style={{ borderLeft: '4px solid #3B82F6' }}
        data-testid={testId}
      >
        <span className="text-gray-700 font-medium">{label}</span>
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg select-all">{displayValue}</span>
          <button
            onClick={handleCopy}
            type="button"
            className={`p-1.5 rounded-lg transition-all ${
              copied ? 'bg-green-100 text-green-700' : 'bg-white text-blue-600 hover:bg-blue-100 shadow-sm'
            }`}
            aria-label={`Copiar ${label}`}
            data-testid={testId ? `${testId}-copy-btn` : undefined}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center py-2 border-b" data-testid={testId}>
      <span className="text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-semibold select-all">{displayValue}</span>
        <button
          onClick={handleCopy}
          type="button"
          className={`p-1 rounded transition-all ${
            copied ? 'text-green-600' : 'text-gray-400 hover:text-blue-600 hover:bg-gray-100'
          }`}
          aria-label={`Copiar ${label}`}
          data-testid={testId ? `${testId}-copy-btn` : undefined}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}
